import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAquariumMemory,
  deleteMemoryItem,
  deleteAllMemory,
  type AquariumMemoryItem,
} from '../services/memoryService';

export const AQUARIUM_MEMORY_QUERY_KEY = 'aquarium-memory';

export interface UseAquariumMemoryResult {
  readonly items: AquariumMemoryItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  readonly deleteItem: (memoryId: string) => Promise<void>;
  readonly isDeletingItem: boolean;
  readonly deleteItemError: Error | null;
  readonly clearAll: () => Promise<void>;
  readonly isClearingAll: boolean;
  readonly clearAllError: Error | null;
}

/**
 * Fetches persisted AI memory items for an aquarium, and exposes mutations to
 * delete a single item or clear all memory. Mirrors the loading/error/empty
 * state contract established by useReefInsight.
 */
export function useAquariumMemory(aquariumId: string): UseAquariumMemoryResult {
  const queryClient = useQueryClient();
  const queryKey = [AQUARIUM_MEMORY_QUERY_KEY, aquariumId];

  const query = useQuery<AquariumMemoryItem[], Error>({
    queryKey,
    queryFn: () => fetchAquariumMemory(aquariumId),
    enabled: Boolean(aquariumId),
  });

  const deleteItemMutation = useMutation<void, Error, string>({
    mutationFn: (memoryId: string) => deleteMemoryItem(aquariumId, memoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const clearAllMutation = useMutation<void, Error, void>({
    mutationFn: () => deleteAllMemory(aquariumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteItem = useCallback(
    async (memoryId: string): Promise<void> => {
      await deleteItemMutation.mutateAsync(memoryId);
    },
    [deleteItemMutation],
  );

  const clearAll = useCallback(async (): Promise<void> => {
    await clearAllMutation.mutateAsync();
  }, [clearAllMutation]);

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: () => {
      void query.refetch();
    },
    deleteItem,
    isDeletingItem: deleteItemMutation.isPending,
    deleteItemError: deleteItemMutation.error ?? null,
    clearAll,
    isClearingAll: clearAllMutation.isPending,
    clearAllError: clearAllMutation.error ?? null,
  };
}
